import { useParams } from "react-router-dom";

import {
  tierHistoryOf,
  toolUsageOf,
  type McpTenantDetail,
  type McpTenantList,
  type McpTierHistory,
  type McpUsage,
} from "../../api/mcp";
import { Blurred } from "../../components/Blurred";
import { series, Spark } from "../../components/charts";
import { BlockControl } from "../../components/mcp/BlockControl";
import { TierControl } from "../../components/mcp/TierControl";
import { RecentChanges } from "../../components/audit/RecentChanges";
import { Breadcrumbs } from "../../components/Layout";
import {
  CouldNotLook,
  Denied,
  LoadFailed,
  Loading,
  NotSeenRecently,
  SignedOut,
} from "../../components/states";
import { Badge, Card, Empty, Field, Fields, Page, Panel, Stat, StatRow, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { NotWired, ProductTag, StoredAs } from "../../products/PerProduct";

export function Tenant({ config }: { config: AppConfig }) {
  const { ref = "" } = useParams();
  const enc = encodeURIComponent(ref);

  const tenant = useResource<McpTenantDetail>(config, `/mcp/admin/tenant/${enc}`, [ref]);
  const usage = useResource<McpUsage>(config, `/mcp/admin/tenant/${enc}/usage`, [ref]);
  const history = useResource<McpTierHistory>(config, `/mcp/admin/tenant/${enc}/history`, [ref]);
  // The REAL list type, not an inline `{ known_tiers: string[] }` — that
  // shape claimed the field is always present, and on a `{}` response
  // TierControl then called `.filter` on undefined and took the page down.
  const list = useResource<McpTenantList>(config, "/mcp/admin/tenants");

  const s = tenant.state;

  return (
    <Page title={<span className="ref">{ref}</span>}>
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tenants", to: "/tenants" },
          { label: ref },
        ]}
      />

      {s.kind === "loading" && <Loading />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook what="this merchant" message={s.error.message} onRetry={tenant.reload} />
      )}
      {s.kind === "error" && <LoadFailed what="this merchant" message={s.error.message} />}

      {/* A 404 still offers the operation that WORKS on a 404 merchant.
          Blocking is keyed by reference and never needs the ledger. */}
      {s.kind === "notfound" && (
        <NotSeenRecently
          reference={ref}
          actions={
            <Button outline href={`/tenants/${enc}/users`}>
              Manage blocks for this reference
            </Button>
          }
        />
      )}

      {s.kind === "ready" &&
        (() => {
          const d = s.data;
          const u = usage.state.kind === "ready" ? usage.state.data : null;
          const h = history.state.kind === "ready" ? history.state.data : null;

          // `blocklist: {blocked, matched}` is GONE from this response — MCP
          // flattened it to `blocked` + `blocked_since`/`blocked_by`/
          // `blocked_reason` (3e43b98). Same three facts the old `matched.
          // merchant_wide` row carried, under their new names.
          const wide = d.blocked
            ? { reason: d.blocked_reason, added_by: d.blocked_by, added_at: d.blocked_since }
            : null;

          return (
            // The Page column already owns the rhythm at `gap-6`; `space-y-5`
            // here set a second, different one, so the tenant record was the
            // only page whose blocks sat closer together than the rest.
            <div className="flex flex-col gap-6">
              {/* The design's IDENTIFIER STRIP: a compact filled band of
                  labelled keys, not rows in the same list as the controls. The
                  identifiers are what you copy into a ticket; the tier picker
                  and the block button are what you act with, and interleaving
                  them in one `dl` made a two-line field list eight rows long
                  and buried both. */}
              <div className="flex flex-wrap gap-x-8 gap-y-3 rounded-lg bg-slate-50 p-4 ring-1 ring-slate-950/5 dark:bg-white/5 dark:ring-white/10">
                {[
                  ["Name", d.business_name, false],
                  ["business_reference", d.business_reference, true],
                  ["Owner email", d.email, true],
                ].map(([label, value, mono]) => (
                  <div key={label as string} className="min-w-0">
                    <p className="text-[10px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                      {label as string}
                    </p>
                    <p
                      className={`mt-0.5 text-sm text-slate-950 dark:text-white ${mono ? "font-mono" : ""}`}
                      dir="auto"
                    >
                      {value as string}
                    </p>
                  </div>
                ))}
              </div>

              {/* Tier and access, one card each, side by side — the design's
                  own grid. PER PRODUCT, because a merchant holds a tier in each
                  and the two can disagree: the design calls that a `tier split`
                  and flags it amber. Only MCP's half is readable here, so the
                  split cannot be computed, and saying which product a value
                  belongs to is what stops it being read as the merchant's tier
                  outright. */}
              <div className="grid gap-4 lg:grid-cols-2">
                <Card title="Plan tier" aside={<ProductTag id="mcp" />}>
                  <Fields>
                    <Field label="MCP">
                      <Badge tone={d.tier ? "neutral" : "amber"}>{d.effective_tier}</Badge>
                      {/* `tier: null` means nothing is stored. The server
                          treats them as free, but reporting the stored value
                          honestly is the point — a GET never creates the row. */}
                      {d.tier_is_implicit ? (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Nothing is stored for this merchant. The server treats them as{" "}
                          <span className="ref">{d.effective_tier}</span>; reading this page
                          does not create the row.
                        </p>
                      ) : (
                        d.tier_source && (
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Stored as <span className="ref">{d.tier}</span>, set by{" "}
                            <span className="ref">{d.tier_source.replace(/^admin:/, "")}</span>
                            {d.tier_updated_at &&
                              ` on ${new Date(d.tier_updated_at).toLocaleDateString()}`}
                          </p>
                        )
                      )}
                    </Field>
                    <Field label="Labeeb">
                      <NotWired id="labeeb" what="The tier this merchant holds in Labeeb" />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Until this reads, a <span className="ref">tier split</span> — the
                        merchant on Pro in one product and Plus in the other — cannot be
                        detected from this console.
                      </p>
                    </Field>
                    <Field label="Stored as">
                      <StoredAs stored={{ mcp: d.tier }} />
                    </Field>
                  </Fields>

                  <div className="mt-4">
                    <TierControl
                      config={config}
                      reference={d.business_reference}
                      currentTier={d.effective_tier}
                      knownTiers={
                        list.state.kind === "ready" ? (list.state.data.known_tiers ?? []) : []
                      }
                      onDone={() => {
                        tenant.reload();
                        history.reload();
                      }}
                    />
                  </div>
                </Card>

                <Card title="Access" aside={<ProductTag id="mcp" />}>
                  <Fields>
                    <Field label="MCP">
                      {d.blocked ? (
                        <Badge tone="red">Blocked</Badge>
                      ) : (
                        <Badge tone="green">Active</Badge>
                      )}
                      {wide && (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {wide.reason && <>Reason: {wide.reason}. </>}
                          {wide.added_by && (
                            <>
                              Blocked by <span className="ref">{wide.added_by}</span>
                            </>
                          )}
                          {wide.added_at && ` on ${new Date(wide.added_at).toLocaleDateString()}`}
                        </p>
                      )}
                    </Field>
                    <Field label="Labeeb">
                      <NotWired id="labeeb" what="Whether this merchant is suspended in Labeeb" />
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Blocking here stops MCP tool calls only. A merchant blocked here can
                        still be talking to the assistant.
                      </p>
                    </Field>
                  </Fields>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <BlockControl
                      config={config}
                      scope={{ kind: "merchant", reference: d.business_reference }}
                      blocked={d.blocked}
                      onDone={tenant.reload}
                    />
                    <Button plain href={`/tenants/${enc}/users`}>
                      Blocked users
                    </Button>
                  </div>
                </Card>
              </div>

              {/* The panel's action belongs in its HEADER, which is the slot
                  Card gives it and the slot the design uses. As a `justify-end`
                  row above the body it pushed the figures down by its own
                  height and lined up with nothing. */}
              <Card
                title="Today"
                aside={
                  <>
                    <Button plain href={`/tenants/${enc}/usage`}>
                      All usage
                    </Button>
                    <ProductTag id="mcp" />
                  </>
                }
              >
                <StatRow>
                  <Stat value={u?.total_credits ?? "…"} label="Credits today" to={`/tenants/${enc}/usage`} />
                  <Stat
                    value={u ? Object.keys(toolUsageOf(u)).length : "…"}
                    label="Tools used"
                    to={`/tenants/${enc}/usage`}
                  />
                  <Stat value={u?.day ?? "…"} label="UTC day" hint={u?.day_boundary} />
                </StatRow>
              </Card>

              {/* "Effective caps" used to live here, joined to the catalog's
                  resolver warnings. Both `caps` and `unprotected_resolvers`
                  are GONE from this response (3e43b98) — caps live on
                  GET /admin/quota/{tier} now, which is exactly the page this
                  card's own "Tier caps" button already sent someone to. */}
              <Card
                title="Caps"
                note={
                  <>
                    Caps for tier <span className="ref">{d.effective_tier}</span> live on the
                    tier's own page now
                    {d.tier_is_implicit && " (implicit — no tier is stored)"}.
                  </>
                }
                aside={<ProductTag id="mcp" />}
              >
                <Button href={`/policy/caps/${encodeURIComponent(d.effective_tier)}`}>
                  Open {d.effective_tier} caps
                </Button>
              </Card>

              <Card
                title="Recent tier changes"
                aside={
                  <>
                    <Button plain href={`/tenants/${enc}/history`}>
                      Full history
                    </Button>
                    <ProductTag id="mcp" />
                  </>
                }
              >
                {tierHistoryOf(h).length > 0 ? (
                  <Table head={<tr><Th>Tier</Th><Th>Set by</Th><Th>When</Th></tr>}>
                    {tierHistoryOf(h).slice(0, 3).map((e, i) => (
                      <tr key={i}>
                        <Td>
                          <Badge>{e.tier}</Badge>
                        </Td>
                        <Td>
                          {e.source === null ? (
                            <span className="text-xs text-slate-500 dark:text-slate-400">unknown</span>
                          ) : e.source === "signup" ? (
                            <span className="text-xs text-slate-500 dark:text-slate-400">automatic — first use</span>
                          ) : (
                            <span className="ref text-xs">{e.source.replace(/^admin:/, "")}</span>
                          )}
                        </Td>
                        <Td>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                          </span>
                        </Td>
                      </tr>
                    ))}
                  </Table>
                ) : (
                  <Empty>No recorded changes.</Empty>
                )}
              </Card>

              {/* The Labeeb half of this record.
                  The design puts a tenant's envelope, effective agents, the
                  deployed stable set and its HITL gates beside MCP's tier and
                  caps — that is the whole point of a per-tenant page in a
                  two-product console. None of it is reachable: no Labeeb admin
                  Lambda publishes a lambda_arn, so the gateway mounts no
                  /labeeb route. Drawn blurred so the record's real shape is
                  visible and no figure on it can be read. */}
              <Card title="Labeeb · this tenant" aside={<ProductTag id="labeeb" />}>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Blurred
                    what={`Tier envelope · ${d.effective_tier}`}
                    needs="Labeeb's admin API"
                    minHeight={180}
                  >
                    <Panel className="p-4">
                      <Fields>
                        {["Daily messages", "Monthly messages", "Monthly tokens", "Snowflake queries"].map(
                          (k) => (
                            <Field key={k} label={k}>
                              <span className="tabular-nums">————</span>
                            </Field>
                          ),
                        )}
                      </Fields>
                    </Panel>
                  </Blurred>

                  <Blurred
                    what="Effective agents · the resolver"
                    needs="Labeeb's admin API"
                    minHeight={180}
                  >
                    <Panel className="p-4">
                      <div className="flex flex-col gap-2">
                        {Array.from({ length: 5 }, (_, i) => (
                          <div key={i} className="flex items-center justify-between gap-3">
                            <div className="h-3 w-32 rounded bg-slate-950/10 dark:bg-white/10" />
                            <div className="h-5 w-12 rounded-full bg-slate-950/5 dark:bg-white/5" />
                          </div>
                        ))}
                      </div>
                    </Panel>
                  </Blurred>

                  <Blurred
                    what="stable_agents · set at deploy"
                    needs="Labeeb's admin API"
                    minHeight={140}
                  >
                    <Panel className="p-4">
                      <div className="flex flex-wrap gap-1.5">
                        {Array.from({ length: 9 }, (_, i) => (
                          <span
                            key={i}
                            className="h-5 rounded-full bg-slate-950/5 dark:bg-white/10"
                            style={{ width: `${60 + ((i * 17) % 40)}px` }}
                          />
                        ))}
                      </div>
                    </Panel>
                  </Blurred>

                  <Blurred what="HITL gates · this tenant" needs="Labeeb's admin API" minHeight={140}>
                    <Panel className="p-4">
                      <div className="flex flex-col gap-3">
                        {Array.from({ length: 4 }, (_, i) => (
                          <div key={i} className="flex items-center justify-between gap-3">
                            <div className="h-3 w-28 rounded bg-slate-950/10 dark:bg-white/10" />
                            <div className="h-6 w-10 rounded-full bg-slate-950/10 dark:bg-white/10" />
                          </div>
                        ))}
                      </div>
                    </Panel>
                  </Blurred>
                </div>

                {/* Consumption is its own row: the design charts it against the
                    envelope over 30 days, and MCP serves only the CURRENT UTC
                    day — so the comparison the panel exists to make cannot be
                    drawn, even though today's number is real above. */}
                <div className="mt-4">
                  <Blurred
                    what="Consumption against the envelope · 30 days"
                    needs="Labeeb's admin API for the envelope, and a usage history route"
                    minHeight={150}
                  >
                    <Panel className="p-4">
                      <Spark values={series(`placeholder-${ref}`, 30, 40)} height={80} />
                    </Panel>
                  </Blurred>
                </div>
              </Card>
            </div>
          );
        })()}
      <div className="mt-4">
        <RecentChanges config={config} domain="tenant" target={ref} />
      </div>

    </Page>
  );
}
